import { useListUpdateCycles, useListUpdateItems } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Clock, Eye, Send, ChevronRight, AlertTriangle } from "lucide-react";
import InfoTip from "@/components/info-tip";

const statusStyles: Record<string, string> = {
  pending: "bg-gray-100 text-gray-800",
  submitted: "bg-blue-100 text-blue-800",
  reviewed: "bg-amber-100 text-amber-800",
  founder_approved: "bg-emerald-100 text-emerald-800",
  published: "bg-primary/10 text-primary",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  submitted: "Submitted",
  reviewed: "Reviewed",
  founder_approved: "Founder Approved",
  published: "Published",
};

const itemTypeStyles: Record<string, string> = {
  fact: "bg-blue-50 text-blue-700 border-blue-200",
  kpi_change: "bg-emerald-50 text-emerald-700 border-emerald-200",
  milestone: "bg-violet-50 text-violet-700 border-violet-200",
  risk: "bg-red-50 text-red-700 border-red-200",
  ask: "bg-amber-50 text-amber-700 border-amber-200",
  media: "bg-pink-50 text-pink-700 border-pink-200",
};

export default function ReviewQueue({ cohortSlug }: { cohortSlug?: string } = {}) {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedCycle, setExpandedCycle] = useState<number | null>(null);
  const { toast } = useToast();

  const { data: cycles, isLoading: cyclesLoading, refetch: refetchCycles } = useListUpdateCycles({
    status: statusFilter !== "all" ? statusFilter as any : undefined,
  });

  const { data: items } = useListUpdateItems({
    cycleId: expandedCycle || undefined,
  });

  const handleTransition = async (cycleId: number, action: string) => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/update-cycles/${cycleId}/transition`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, reviewedBy: "operator" }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Transition failed");
      }
      toast({ title: "Cycle updated", description: `Transitioned to ${action}` });
      refetchCycles();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleApproveItem = async (itemId: number, visibility: string) => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/update-items/${itemId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visibility, approvedBy: "operator" }),
      });
      if (!res.ok) throw new Error("Approval failed");
      toast({ title: "Item approved", description: `Visibility set to ${visibility}` });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const nextAction = (status: string): { label: string; action: string; icon: typeof Send } | null => {
    switch (status) {
      case "submitted": return { label: "Mark Reviewed", action: "review", icon: Eye };
      case "reviewed": return { label: "Send to Founder", action: "founder_approve", icon: Send };
      case "founder_approved": return { label: "Publish to Investors", action: "publish", icon: CheckCircle2 };
      default: return null;
    }
  };

  return (
    <div className="space-y-8" data-testid="page-review-queue">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Review Queue</h1>
            <InfoTip content="Every company update goes through a 5-step process: Pending (drafted) → Submitted (by founder) → Reviewed (by ops team) → Founder Approved (founder signs off) → Published (visible to investors). This queue lets you move updates through each step." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Update cycle state machine &mdash; review, approve, publish</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="founder_approved">Founder Approved</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {cyclesLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : !cycles?.length ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">No update cycles found</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {cycles.map((cycle: any) => {
            const action = nextAction(cycle.status);
            const isExpanded = expandedCycle === cycle.id;
            return (
              <Card key={cycle.id} className={isExpanded ? "ring-1 ring-primary/30" : ""}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setExpandedCycle(isExpanded ? null : cycle.id)}
                        className="p-1 hover:bg-muted rounded"
                      >
                        <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{cycle.companyName || `Company #${cycle.companyId}`}</span>
                          <Badge variant="outline" className={statusStyles[cycle.status]}>
                            {statusLabels[cycle.status] || cycle.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {cycle.cycleType} &middot; Due {new Date(cycle.dueDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {cycle.status === "pending" && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Awaiting submission
                        </span>
                      )}
                      {action && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleTransition(cycle.id, action.action)}
                          className="text-xs"
                        >
                          <action.icon className="w-3 h-3 mr-1" />
                          {action.label}
                        </Button>
                      )}
                    </div>
                  </div>

                  {isExpanded && items && (
                    <div className="mt-4 border-t pt-4 space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Update Items</p>
                      {items.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No items in this cycle yet</p>
                      ) : (
                        items.map((item: any) => (
                          <div key={item.id} className={`p-3 rounded-md border text-sm ${itemTypeStyles[item.type] || "bg-muted"}`}>
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-[10px]">{item.type}</Badge>
                                  <Badge variant="outline" className="text-[10px]">{item.visibility}</Badge>
                                  <Badge variant="outline" className="text-[10px]">{item.approvalState}</Badge>
                                </div>
                                <p className="text-sm">{item.summary}</p>
                              </div>
                              {item.approvalState === "draft" && (
                                <div className="flex gap-1 ml-4 flex-shrink-0">
                                  <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => handleApproveItem(item.id, "founder")}>
                                    Founder
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => handleApproveItem(item.id, "investor")}>
                                    Investor
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
